import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus, Sparkles, ArrowUp, ArrowDown, Edit3, Trash2, Video,
    Image as ImageIcon, Eye, EyeOff, Layers, ExternalLink, RefreshCw,
    LayoutTemplate, Grid, Award, LayoutList
} from 'lucide-react';
import {
    BannerSlide,
    fetchBannerSlidesAdmin,
    reorderBannerSlides,
    updateBannerSlide,
    deleteBannerSlide,
} from '../../../services/landing/bannerSlideService';
import {
    LandingEspacio,
    LandingExperiencia,
    LandingSplitSectionItem,
    fetchEspaciosAdmin,
    reorderEspacios,
    updateEspacio,
    deleteEspacio,
    fetchExperienciaAdmin,
    reorderExperiencia,
    updateExperiencia,
    deleteExperiencia,
    fetchSplitSectionsAdmin,
    reorderSplitSections,
    updateSplitSection,
    deleteSplitSection,
} from '../../../services/landing/landingSectionsService';

import AddBannerSlideModal from '../../../components/BannerHero/AddBannerSlideModal';
import EditBannerSlideModal from '../../../components/BannerHero/EditBannerSlideModal';
import DeleteBannerSlideModal from '../../../components/BannerHero/DeleteBannerSlideModal';
import { EspacioModal } from '../../../components/LandingSections/EspacioModal';
import { ExperienciaModal } from '../../../components/LandingSections/ExperienciaModal';
import { SplitSectionModal } from '../../../components/LandingSections/SplitSectionModal';
import { DeleteLandingItemModal } from '../../../components/LandingSections/DeleteLandingItemModal';
import Loading from '../../../components/Loading';

type ActiveTab = 'hero' | 'espacios' | 'experiencia' | 'split';

export const BannerHeroAdminPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<ActiveTab>('hero');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // ═══ TAB 1: HERO SLIDES ═══
    const [slides, setSlides] = useState<BannerSlide[]>([]);
    const [isAddSlideOpen, setIsAddSlideOpen] = useState(false);
    const [isEditSlideOpen, setIsEditSlideOpen] = useState(false);
    const [isDeleteSlideOpen, setIsDeleteSlideOpen] = useState(false);
    const [selectedSlide, setSelectedSlide] = useState<BannerSlide | null>(null);

    // ═══ TAB 2: ESPACIOS (ACORDEÓN) ═══
    const [espacios, setEspacios] = useState<LandingEspacio[]>([]);
    const [isEspacioModalOpen, setIsEspacioModalOpen] = useState(false);
    const [selectedEspacio, setSelectedEspacio] = useState<LandingEspacio | null>(null);
    const [isDeleteEspacioOpen, setIsDeleteEspacioOpen] = useState(false);

    // ═══ TAB 3: CARRUSEL EXPERIENCIA ═══
    const [experiencias, setExperiencias] = useState<LandingExperiencia[]>([]);
    const [isExperienciaModalOpen, setIsExperienciaModalOpen] = useState(false);
    const [selectedExperiencia, setSelectedExperiencia] = useState<LandingExperiencia | null>(null);
    const [isDeleteExperienciaOpen, setIsDeleteExperienciaOpen] = useState(false);

    // ═══ TAB 4: SECCIONES DIVIDIDAS (SPLIT) ═══
    const [splitSections, setSplitSections] = useState<LandingSplitSectionItem[]>([]);
    const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
    const [selectedSplit, setSelectedSplit] = useState<LandingSplitSectionItem | null>(null);
    const [isDeleteSplitOpen, setIsDeleteSplitOpen] = useState(false);

    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 4000);
    };

    // Load data depending on active tab or all
    const loadAll = useCallback(async () => {
        try {
            setLoading(true);
            const [slidesData, espaciosData, expData, splitData] = await Promise.all([
                fetchBannerSlidesAdmin().catch(() => []),
                fetchEspaciosAdmin().catch(() => []),
                fetchExperienciaAdmin().catch(() => []),
                fetchSplitSectionsAdmin().catch(() => []),
            ]);
            setSlides(slidesData.sort((a, b) => a.orden - b.orden));
            setEspacios(espaciosData.sort((a, b) => a.orden - b.orden));
            setExperiencias(expData.sort((a, b) => a.orden - b.orden));
            setSplitSections(splitData.sort((a, b) => a.orden - b.orden));
        } catch (error) {
            console.error('Error al cargar datos del gestor de landing:', error);
            showMessage('error', 'Error al sincronizar datos del landing');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // ──────────────────────────────────────────
    // HANDLERS HERO SLIDES
    // ──────────────────────────────────────────
    const handleToggleSlideActivo = async (slide: BannerSlide) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!slide.activo));
            await updateBannerSlide(slide.id, formData);
            setSlides((prev) =>
                prev.map((s) => (s.id === slide.id ? { ...s, activo: !s.activo } : s))
            );
            showMessage('success', `Slide #${slide.orden} ${!slide.activo ? 'activado' : 'desactivado'}.`);
        } catch {
            showMessage('error', 'No se pudo cambiar el estado del slide.');
        }
    };

    const handleMoveSlide = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= slides.length) return;
        setActionLoading(true);
        const newSlides = [...slides];
        const temp = newSlides[index];
        newSlides[index] = newSlides[targetIndex];
        newSlides[targetIndex] = temp;
        const reorderPayload = newSlides.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setSlides(newSlides.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderBannerSlides(reorderPayload);
            showMessage('success', 'Orden de diapositivas actualizado.');
        } catch {
            showMessage('error', 'Error al guardar el nuevo orden.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    // ──────────────────────────────────────────
    // HANDLERS ESPACIOS (ACORDEÓN)
    // ──────────────────────────────────────────
    const handleToggleEspacioActivo = async (item: LandingEspacio) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!item.activo));
            await updateEspacio(item.id, formData);
            setEspacios((prev) =>
                prev.map((e) => (e.id === item.id ? { ...e, activo: !e.activo } : e))
            );
            showMessage('success', `Espacio "${item.label}" actualizado.`);
        } catch {
            showMessage('error', 'Error al actualizar estado del espacio.');
        }
    };

    const handleMoveEspacio = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= espacios.length) return;
        setActionLoading(true);
        const newItems = [...espacios];
        const temp = newItems[index];
        newItems[index] = newItems[targetIndex];
        newItems[targetIndex] = temp;
        const payload = newItems.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setEspacios(newItems.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderEspacios(payload);
            showMessage('success', 'Nuevo orden de espacios guardado.');
        } catch {
            showMessage('error', 'Error al reordenar espacios.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteEspacioConfirm = async () => {
        if (!selectedEspacio) return;
        try {
            setActionLoading(true);
            await deleteEspacio(selectedEspacio.id);
            setEspacios((prev) => prev.filter((e) => e.id !== selectedEspacio.id));
            showMessage('success', 'Espacio eliminado correctamente.');
            setIsDeleteEspacioOpen(false);
            setSelectedEspacio(null);
        } catch {
            showMessage('error', 'Error al eliminar espacio.');
        } finally {
            setActionLoading(false);
        }
    };

    // ──────────────────────────────────────────
    // HANDLERS CARRUSEL EXPERIENCIA
    // ──────────────────────────────────────────
    const handleToggleExperienciaActivo = async (item: LandingExperiencia) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!item.activo));
            await updateExperiencia(item.id, formData);
            setExperiencias((prev) =>
                prev.map((e) => (e.id === item.id ? { ...e, activo: !e.activo } : e))
            );
            showMessage('success', `Tarjeta "${item.titulo.replace('\n', ' ')}" actualizada.`);
        } catch {
            showMessage('error', 'Error al actualizar tarjeta.');
        }
    };

    const handleMoveExperiencia = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= experiencias.length) return;
        setActionLoading(true);
        const newItems = [...experiencias];
        const temp = newItems[index];
        newItems[index] = newItems[targetIndex];
        newItems[targetIndex] = temp;
        const payload = newItems.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setExperiencias(newItems.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderExperiencia(payload);
            showMessage('success', 'Nuevo orden del carrusel guardado.');
        } catch {
            showMessage('error', 'Error al reordenar carrusel.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteExperienciaConfirm = async () => {
        if (!selectedExperiencia) return;
        try {
            setActionLoading(true);
            await deleteExperiencia(selectedExperiencia.id);
            setExperiencias((prev) => prev.filter((e) => e.id !== selectedExperiencia.id));
            showMessage('success', 'Slide de experiencia eliminado.');
            setIsDeleteExperienciaOpen(false);
            setSelectedExperiencia(null);
        } catch {
            showMessage('error', 'Error al eliminar slide.');
        } finally {
            setActionLoading(false);
        }
    };

    // ──────────────────────────────────────────
    // HANDLERS SECCIONES DIVIDIDAS (SPLIT)
    // ──────────────────────────────────────────
    const handleToggleSplitActivo = async (item: LandingSplitSectionItem) => {
        try {
            const formData = new FormData();
            formData.append('activo', String(!item.activo));
            await updateSplitSection(item.id, formData);
            setSplitSections((prev) =>
                prev.map((s) => (s.id === item.id ? { ...s, activo: !s.activo } : s))
            );
            showMessage('success', `Sección "${item.titulo}" actualizada.`);
        } catch {
            showMessage('error', 'Error al actualizar sección dividida.');
        }
    };

    const handleMoveSplit = async (index: number, direction: 'up' | 'down') => {
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= splitSections.length) return;
        setActionLoading(true);
        const newItems = [...splitSections];
        const temp = newItems[index];
        newItems[index] = newItems[targetIndex];
        newItems[targetIndex] = temp;
        const payload = newItems.map((item, idx) => ({ id: item.id, orden: idx + 1 }));
        setSplitSections(newItems.map((item, idx) => ({ ...item, orden: idx + 1 })));
        try {
            await reorderSplitSections(payload);
            showMessage('success', 'Nuevo orden de secciones guardado.');
        } catch {
            showMessage('error', 'Error al reordenar secciones.');
            loadAll();
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteSplitConfirm = async () => {
        if (!selectedSplit) return;
        try {
            setActionLoading(true);
            await deleteSplitSection(selectedSplit.id);
            setSplitSections((prev) => prev.filter((s) => s.id !== selectedSplit.id));
            showMessage('success', 'Sección informativa eliminada.');
            setIsDeleteSplitOpen(false);
            setSelectedSplit(null);
        } catch {
            showMessage('error', 'Error al eliminar sección.');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return <Loading text="Cargando gestor de secciones del Landing Page..." />;
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in font-['DM_Sans',sans-serif]">
            {/* Header del Centro de Control */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 mb-8 border-b border-gray-200 dark:border-purple-900/30">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 dark:bg-purple-950/60 text-[#61257B] dark:text-purple-300 mb-2 border border-purple-200 dark:border-purple-800/40">
                        <Sparkles size={14} />
                        Gestor Integral del Landing Page
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                        Personalización Visual de la Landing
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                        Edita imágenes, videos, textos, estadísticas y el orden de aparición de todas las secciones principales de la portada.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={loadAll}
                        disabled={actionLoading}
                        className="p-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-purple-950/40 transition-colors"
                        title="Actualizar datos"
                    >
                        <RefreshCw size={17} className={actionLoading ? 'animate-spin' : ''} />
                    </button>
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-purple-200 dark:border-purple-900/40 text-[#61257B] dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/30 font-semibold text-xs transition-colors"
                    >
                        <ExternalLink size={15} />
                        Ver Landing en Vivo
                    </a>
                </div>
            </div>

            {/* Mensaje de alerta */}
            {message && (
                <div
                    className={`mb-6 p-4 rounded-xl border flex items-center justify-between text-sm animate-fade-in ${
                        message.type === 'success'
                            ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950/30 dark:border-green-900/50 dark:text-green-300'
                            : 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950/30 dark:border-red-900/50 dark:text-red-300'
                    }`}
                >
                    <span>{message.text}</span>
                    <button onClick={() => setMessage(null)} className="text-xs font-bold underline ml-4">
                        Cerrar
                    </button>
                </div>
            )}

            {/* ═══ TABS DE NAVEGACIÓN ═══ */}
            <div className="flex items-center gap-2 border-b border-gray-200 dark:border-purple-900/30 mb-8 overflow-x-auto pb-1">
                <button
                    onClick={() => setActiveTab('hero')}
                    className={`flex items-center gap-2.5 py-3 px-5 font-bold text-sm rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                        activeTab === 'hero'
                            ? 'border-[#c1348b] text-[#c1348b] bg-purple-50/60 dark:bg-purple-950/30'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                    <LayoutTemplate size={17} />
                    <span>Banner Principal (Hero)</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#c1348b]/15 text-[#c1348b] font-bold">
                        {slides.length}
                    </span>
                </button>

                <button
                    onClick={() => setActiveTab('espacios')}
                    className={`flex items-center gap-2.5 py-3 px-5 font-bold text-sm rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                        activeTab === 'espacios'
                            ? 'border-[#c1348b] text-[#c1348b] bg-purple-50/60 dark:bg-purple-950/30'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                    <Grid size={17} />
                    <span>Galería de Espacios (Acordeón)</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#61257B]/15 text-[#61257B] dark:text-purple-300 font-bold">
                        {espacios.length}
                    </span>
                </button>

                <button
                    onClick={() => setActiveTab('experiencia')}
                    className={`flex items-center gap-2.5 py-3 px-5 font-bold text-sm rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                        activeTab === 'experiencia'
                            ? 'border-[#c1348b] text-[#c1348b] bg-purple-50/60 dark:bg-purple-950/30'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                    <Award size={17} />
                    <span>Carrusel Logros & Aspiración (3D)</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#61257B]/15 text-[#61257B] dark:text-purple-300 font-bold">
                        {experiencias.length}
                    </span>
                </button>

                <button
                    onClick={() => setActiveTab('split')}
                    className={`flex items-center gap-2.5 py-3 px-5 font-bold text-sm rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                        activeTab === 'split'
                            ? 'border-[#c1348b] text-[#c1348b] bg-purple-50/60 dark:bg-purple-950/30'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                >
                    <LayoutList size={17} />
                    <span>Secciones Divididas (Split)</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#61257B]/15 text-[#61257B] dark:text-purple-300 font-bold">
                        {splitSections.length}
                    </span>
                </button>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* TAB 1: HERO BANNER (SLIDES CON FOTO / VIDEO)               */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {activeTab === 'hero' && (
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Diapositivas del Hero Banner
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Desliza o usa las flechas ⬆️ / ⬇️ para cambiar el orden en que se presentarán en la portada.
                            </p>
                        </div>
                        <button
                            onClick={() => setIsAddSlideOpen(true)}
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#61257B] to-[#c1348b] hover:opacity-95 text-white font-bold text-xs shadow-lg shadow-purple-900/20 transition-all cursor-pointer"
                        >
                            <Plus size={16} />
                            Nueva Diapositiva (Hero)
                        </button>
                    </div>

                    <div className="bg-white dark:bg-[#120a21] rounded-2xl border border-gray-200 dark:border-purple-900/30 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-purple-900/20">
                                <thead className="bg-gray-50 dark:bg-purple-950/20">
                                    <tr>
                                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Orden</th>
                                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Vista previa</th>
                                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Título & Acento</th>
                                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descripción</th>
                                        <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Botones CTA</th>
                                        <th className="px-5 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                                        <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-purple-900/10 text-sm">
                                    {slides.map((slide, index) => {
                                        const mediaSrc = slide.url_imagen || slide.imagen;
                                        const isVideo = slide.tipo === 'video';
                                        return (
                                            <tr key={slide.id} className="hover:bg-gray-50/70 dark:hover:bg-purple-950/10 transition-colors">
                                                <td className="px-5 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-purple-950/40 text-gray-700 dark:text-gray-300 font-bold text-xs flex items-center justify-center">
                                                            {slide.orden}
                                                        </span>
                                                        <div className="flex flex-col">
                                                            <button
                                                                onClick={() => handleMoveSlide(index, 'up')}
                                                                disabled={index === 0 || actionLoading}
                                                                className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                            >
                                                                <ArrowUp size={13} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleMoveSlide(index, 'down')}
                                                                disabled={index === slides.length - 1 || actionLoading}
                                                                className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                            >
                                                                <ArrowDown size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 whitespace-nowrap">
                                                    <div className="relative w-24 h-14 rounded-lg overflow-hidden border border-gray-200 dark:border-purple-900/30 bg-black/40 flex items-center justify-center">
                                                        {isVideo ? (
                                                            <div className="w-full h-full flex flex-col items-center justify-center bg-purple-950/60 text-purple-200">
                                                                <Video size={18} />
                                                                <span className="text-[10px] mt-0.5 font-bold uppercase">Video</span>
                                                            </div>
                                                        ) : mediaSrc ? (
                                                            <img src={mediaSrc} alt={slide.titulo} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="text-gray-400"><ImageIcon size={18} /></div>
                                                        )}
                                                        <span className={`absolute top-1 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${isVideo ? 'bg-purple-700 text-white' : 'bg-gray-800/80 text-white'}`}>
                                                            {isVideo ? 'MP4' : 'IMG'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <div className="font-extrabold text-gray-900 dark:text-white">
                                                        {slide.titulo}{' '}
                                                        <span style={{ color: slide.color_acento || '#c1348b' }}>
                                                            {slide.titulo_acento}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-5 py-4 max-w-xs">
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                                        {slide.descripcion}
                                                    </p>
                                                </td>
                                                <td className="px-5 py-4 text-xs whitespace-nowrap">
                                                    <div className="text-gray-700 dark:text-gray-300 font-semibold">1: {slide.texto_boton_primario}</div>
                                                    <div className="text-gray-500 dark:text-gray-400">2: {slide.texto_boton_secundario}</div>
                                                </td>
                                                <td className="px-5 py-4 text-center whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleToggleSlideActivo(slide)}
                                                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                                                            slide.activo
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300'
                                                                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                        }`}
                                                    >
                                                        {slide.activo ? <Eye size={12} /> : <EyeOff size={12} />}
                                                        {slide.activo ? 'Activo' : 'Inactivo'}
                                                    </button>
                                                </td>
                                                <td className="px-5 py-4 text-right whitespace-nowrap">
                                                    <div className="inline-flex items-center gap-1">
                                                        <button
                                                            onClick={() => { setSelectedSlide(slide); setIsEditSlideOpen(true); }}
                                                            className="p-1.5 text-gray-500 hover:text-[#61257B] dark:hover:text-purple-300 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-950/40 transition-colors"
                                                            title="Editar slide"
                                                        >
                                                            <Edit3 size={15} />
                                                        </button>
                                                        <button
                                                            onClick={() => { setSelectedSlide(slide); setIsDeleteSlideOpen(true); }}
                                                            className="p-1.5 text-gray-500 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                                                            title="Eliminar slide"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* TAB 2: GALERÍA DE ESPACIOS (ACORDEÓN INTERACTIVO)          */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {activeTab === 'espacios' && (
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Galería Acordeón ("Sobre Nosotros")
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Administra las tarjetas fotográficas que se expanden al pasar el cursor en la sección de presentación.
                            </p>
                        </div>
                        <button
                            onClick={() => { setSelectedEspacio(null); setIsEspacioModalOpen(true); }}
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#61257B] to-[#c1348b] hover:opacity-95 text-white font-bold text-xs shadow-lg shadow-purple-900/20 transition-all cursor-pointer"
                        >
                            <Plus size={16} />
                            Nuevo Espacio en Galería
                        </button>
                    </div>

                    <div className="bg-white dark:bg-[#120a21] rounded-2xl border border-gray-200 dark:border-purple-900/30 shadow-sm overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-purple-900/20">
                            <thead className="bg-gray-50 dark:bg-purple-950/20">
                                <tr>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Orden</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fotografía</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nombre del Espacio</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Texto Alternativo</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                                    <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-purple-900/10 text-sm">
                                {espacios.map((item, index) => {
                                    const imgSrc = item.url_imagen || item.imagen;
                                    return (
                                        <tr key={item.id} className="hover:bg-gray-50/70 dark:hover:bg-purple-950/10 transition-colors">
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-purple-950/40 text-gray-700 dark:text-gray-300 font-bold text-xs flex items-center justify-center">
                                                        {item.orden}
                                                    </span>
                                                    <div className="flex flex-col">
                                                        <button
                                                            onClick={() => handleMoveEspacio(index, 'up')}
                                                            disabled={index === 0 || actionLoading}
                                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                        >
                                                            <ArrowUp size={13} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleMoveEspacio(index, 'down')}
                                                            disabled={index === espacios.length - 1 || actionLoading}
                                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                        >
                                                            <ArrowDown size={13} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="w-20 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-purple-900/30 bg-gray-100 dark:bg-purple-950/30">
                                                    {imgSrc ? (
                                                        <img src={imgSrc} alt={item.label} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">Sin foto</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 font-bold text-gray-900 dark:text-white">
                                                {item.label}
                                            </td>
                                            <td className="px-5 py-4 text-xs text-gray-500 dark:text-gray-400">
                                                {item.alt || item.label}
                                            </td>
                                            <td className="px-5 py-4 text-center whitespace-nowrap">
                                                <button
                                                    onClick={() => handleToggleEspacioActivo(item)}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                                                        item.activo
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300'
                                                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                    }`}
                                                >
                                                    {item.activo ? <Eye size={12} /> : <EyeOff size={12} />}
                                                    {item.activo ? 'Activo' : 'Inactivo'}
                                                </button>
                                            </td>
                                            <td className="px-5 py-4 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        onClick={() => { setSelectedEspacio(item); setIsEspacioModalOpen(true); }}
                                                        className="p-1.5 text-gray-500 hover:text-[#61257B] rounded-lg hover:bg-gray-100 dark:hover:bg-purple-950/40 transition-colors"
                                                    >
                                                        <Edit3 size={15} />
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedEspacio(item); setIsDeleteEspacioOpen(true); }}
                                                        className="p-1.5 text-gray-500 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* TAB 3: CARRUSEL EXPERIENCIA & LOGROS (DEPTHCAROUSEL 3D)    */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {activeTab === 'experiencia' && (
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Carrusel de Hitos & Aspiración (DepthCarousel 3D)
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Tarjetas interactivas con perspectiva tridimensional y estadísticas de crecimiento (5+ Años, +120 Proyectos, Visión).
                            </p>
                        </div>
                        <button
                            onClick={() => { setSelectedExperiencia(null); setIsExperienciaModalOpen(true); }}
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#61257B] to-[#c1348b] hover:opacity-95 text-white font-bold text-xs shadow-lg shadow-purple-900/20 transition-all cursor-pointer"
                        >
                            <Plus size={16} />
                            Nuevo Slide de Experiencia
                        </button>
                    </div>

                    <div className="bg-white dark:bg-[#120a21] rounded-2xl border border-gray-200 dark:border-purple-900/30 shadow-sm overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-purple-900/20">
                            <thead className="bg-gray-50 dark:bg-purple-950/20">
                                <tr>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Orden</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarjeta 3D</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Métrica</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Título & Tag</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descripción</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                                    <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-purple-900/10 text-sm">
                                {experiencias.map((item, index) => {
                                    const imgSrc = item.url_imagen || item.imagen;
                                    return (
                                        <tr key={item.id} className="hover:bg-gray-50/70 dark:hover:bg-purple-950/10 transition-colors">
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-purple-950/40 text-gray-700 dark:text-gray-300 font-bold text-xs flex items-center justify-center">
                                                        {item.orden}
                                                    </span>
                                                    <div className="flex flex-col">
                                                        <button
                                                            onClick={() => handleMoveExperiencia(index, 'up')}
                                                            disabled={index === 0 || actionLoading}
                                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                        >
                                                            <ArrowUp size={13} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleMoveExperiencia(index, 'down')}
                                                            disabled={index === experiencias.length - 1 || actionLoading}
                                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                        >
                                                            <ArrowDown size={13} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="w-20 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-purple-900/30 bg-gray-100 dark:bg-purple-950/30">
                                                    {imgSrc ? (
                                                        <img src={imgSrc} alt={item.titulo} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">Foto local</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <span
                                                    className="inline-flex flex-col items-center justify-center px-2.5 py-1 rounded-lg text-white font-black text-sm"
                                                    style={{ backgroundColor: item.color_acento || '#61257B' }}
                                                >
                                                    {item.stat}
                                                    <span className="text-[9px] font-normal uppercase opacity-90">{item.stat_label}</span>
                                                </span>
                                            </td>
                                            <td className="px-5 py-4">
                                                <span className="text-[10px] uppercase font-bold text-purple-600 dark:text-purple-300 block">
                                                    {item.tag}
                                                </span>
                                                <span className="font-extrabold text-gray-900 dark:text-white whitespace-pre-line">
                                                    {item.titulo}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 max-w-xs">
                                                <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                                                    {item.descripcion}
                                                </p>
                                            </td>
                                            <td className="px-5 py-4 text-center whitespace-nowrap">
                                                <button
                                                    onClick={() => handleToggleExperienciaActivo(item)}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                                                        item.activo
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300'
                                                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                    }`}
                                                >
                                                    {item.activo ? <Eye size={12} /> : <EyeOff size={12} />}
                                                    {item.activo ? 'Activo' : 'Inactivo'}
                                                </button>
                                            </td>
                                            <td className="px-5 py-4 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        onClick={() => { setSelectedExperiencia(item); setIsExperienciaModalOpen(true); }}
                                                        className="p-1.5 text-gray-500 hover:text-[#61257B] rounded-lg hover:bg-gray-100 dark:hover:bg-purple-950/40 transition-colors"
                                                    >
                                                        <Edit3 size={15} />
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedExperiencia(item); setIsDeleteExperienciaOpen(true); }}
                                                        className="p-1.5 text-gray-500 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* TAB 4: SECCIONES DIVIDIDAS (SPLIT SECTIONS)                */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {activeTab === 'split' && (
                <div>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Secciones Informativas Divididas (Split Sections)
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Bloques informativos de ancho completo con diseño alternado (imagen a la izquierda / derecha), viñetas de propuesta de valor y botón CTA.
                            </p>
                        </div>
                        <button
                            onClick={() => { setSelectedSplit(null); setIsSplitModalOpen(true); }}
                            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#61257B] to-[#c1348b] hover:opacity-95 text-white font-bold text-xs shadow-lg shadow-purple-900/20 transition-all cursor-pointer"
                        >
                            <Plus size={16} />
                            Nueva Sección Dividida
                        </button>
                    </div>

                    <div className="bg-white dark:bg-[#120a21] rounded-2xl border border-gray-200 dark:border-purple-900/30 shadow-sm overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-purple-900/20">
                            <thead className="bg-gray-50 dark:bg-purple-950/20">
                                <tr>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Orden</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Imagen</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Alineación</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Título & Tag</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Viñetas / Bullets</th>
                                    <th className="px-5 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Botón & Enlace</th>
                                    <th className="px-5 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                                    <th className="px-5 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-purple-900/10 text-sm">
                                {splitSections.map((item, index) => {
                                    const imgSrc = item.url_imagen || item.imagen;
                                    const bullets = item.caracteristicas ? item.caracteristicas.split('\n').filter(Boolean) : [];
                                    return (
                                        <tr key={item.id} className="hover:bg-gray-50/70 dark:hover:bg-purple-950/10 transition-colors">
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-purple-950/40 text-gray-700 dark:text-gray-300 font-bold text-xs flex items-center justify-center">
                                                        {item.orden}
                                                    </span>
                                                    <div className="flex flex-col">
                                                        <button
                                                            onClick={() => handleMoveSplit(index, 'up')}
                                                            disabled={index === 0 || actionLoading}
                                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                        >
                                                            <ArrowUp size={13} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleMoveSplit(index, 'down')}
                                                            disabled={index === splitSections.length - 1 || actionLoading}
                                                            className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-20 transition-colors"
                                                        >
                                                            <ArrowDown size={13} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="w-20 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-purple-900/30 bg-gray-100 dark:bg-purple-950/30">
                                                    {imgSrc ? (
                                                        <img src={imgSrc} alt={item.titulo} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">Foto local</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 py-4 whitespace-nowrap text-xs">
                                                <span className={`px-2.5 py-1 rounded-md font-semibold ${
                                                    item.imagen_izquierda
                                                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                                                        : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                                                }`}>
                                                    {item.imagen_izquierda ? '⬅️ Foto Izq' : 'Foto Der ➡️'}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 max-w-xs">
                                                <span className="text-[10px] uppercase font-bold text-purple-600 dark:text-purple-300 block">
                                                    {item.tag}
                                                </span>
                                                <span className="font-extrabold text-gray-900 dark:text-white line-clamp-2">
                                                    {item.titulo}
                                                </span>
                                            </td>
                                            <td className="px-5 py-4 text-xs max-w-xs">
                                                <ul className="list-disc list-inside text-gray-500 dark:text-gray-400 space-y-0.5">
                                                    {bullets.slice(0, 3).map((b, i) => (
                                                        <li key={i} className="truncate">{b}</li>
                                                    ))}
                                                    {bullets.length > 3 && (
                                                        <li className="text-purple-600 font-semibold">+{bullets.length - 3} más...</li>
                                                    )}
                                                </ul>
                                            </td>
                                            <td className="px-5 py-4 text-xs whitespace-nowrap">
                                                <div className="font-semibold text-gray-800 dark:text-gray-200">{item.texto_boton}</div>
                                                <div className="text-purple-600 text-[11px] truncate max-w-[130px]">{item.enlace_boton}</div>
                                            </td>
                                            <td className="px-5 py-4 text-center whitespace-nowrap">
                                                <button
                                                    onClick={() => handleToggleSplitActivo(item)}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                                                        item.activo
                                                            ? 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300'
                                                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                    }`}
                                                >
                                                    {item.activo ? <Eye size={12} /> : <EyeOff size={12} />}
                                                    {item.activo ? 'Activo' : 'Inactivo'}
                                                </button>
                                            </td>
                                            <td className="px-5 py-4 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        onClick={() => { setSelectedSplit(item); setIsSplitModalOpen(true); }}
                                                        className="p-1.5 text-gray-500 hover:text-[#61257B] rounded-lg hover:bg-gray-100 dark:hover:bg-purple-950/40 transition-colors"
                                                    >
                                                        <Edit3 size={15} />
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedSplit(item); setIsDeleteSplitOpen(true); }}
                                                        className="p-1.5 text-gray-500 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ═══ MODALES HERO SLIDE ═══ */}
            <AddBannerSlideModal
                isOpen={isAddSlideOpen}
                onClose={() => setIsAddSlideOpen(false)}
                onSuccess={() => { loadAll(); showMessage('success', 'Diapositiva del Hero agregada.'); }}
                nextOrderNumber={slides.length + 1}
            />
            <EditBannerSlideModal
                isOpen={isEditSlideOpen}
                onClose={() => { setIsEditSlideOpen(false); setSelectedSlide(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Diapositiva actualizada.'); }}
                slide={selectedSlide}
            />
            <DeleteBannerSlideModal
                isOpen={isDeleteSlideOpen}
                onClose={() => { setIsDeleteSlideOpen(false); setSelectedSlide(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Diapositiva eliminada.'); }}
                slide={selectedSlide}
            />

            {/* ═══ MODALES ESPACIOS ═══ */}
            <EspacioModal
                isOpen={isEspacioModalOpen}
                onClose={() => { setIsEspacioModalOpen(false); setSelectedEspacio(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Espacio guardado con éxito.'); }}
                espacioToEdit={selectedEspacio}
            />
            <DeleteLandingItemModal
                isOpen={isDeleteEspacioOpen}
                onClose={() => { setIsDeleteEspacioOpen(false); setSelectedEspacio(null); }}
                onConfirm={handleDeleteEspacioConfirm}
                title="Eliminar Espacio"
                itemName={selectedEspacio?.label || ''}
                loading={actionLoading}
            />

            {/* ═══ MODALES EXPERIENCIA ═══ */}
            <ExperienciaModal
                isOpen={isExperienciaModalOpen}
                onClose={() => { setIsExperienciaModalOpen(false); setSelectedExperiencia(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Slide de experiencia guardado.'); }}
                itemToEdit={selectedExperiencia}
            />
            <DeleteLandingItemModal
                isOpen={isDeleteExperienciaOpen}
                onClose={() => { setIsDeleteExperienciaOpen(false); setSelectedExperiencia(null); }}
                onConfirm={handleDeleteExperienciaConfirm}
                title="Eliminar Slide de Experiencia"
                itemName={selectedExperiencia?.titulo || ''}
                loading={actionLoading}
            />

            {/* ═══ MODALES SECCIONES DIVIDIDAS ═══ */}
            <SplitSectionModal
                isOpen={isSplitModalOpen}
                onClose={() => { setIsSplitModalOpen(false); setSelectedSplit(null); }}
                onSuccess={() => { loadAll(); showMessage('success', 'Sección informativa guardada.'); }}
                itemToEdit={selectedSplit}
            />
            <DeleteLandingItemModal
                isOpen={isDeleteSplitOpen}
                onClose={() => { setIsDeleteSplitOpen(false); setSelectedSplit(null); }}
                onConfirm={handleDeleteSplitConfirm}
                title="Eliminar Sección Informativa"
                itemName={selectedSplit?.titulo || ''}
                loading={actionLoading}
            />
        </div>
    );
};

export default BannerHeroAdminPage;
